import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Link,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  Email,
  Lock,
} from "@mui/icons-material";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  padding: theme.spacing(2),
}));

const LoginCard = styled(Card)(({ theme }) => ({
  maxWidth: 400,
  width: "100%",
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[10],
}));

const SocialButton = styled(Button)(({ theme }) => ({
  height: 48,
  borderRadius: theme.spacing(1),
  textTransform: "none",
  fontSize: "0.95rem",
  fontWeight: 500,
}));

const GoogleButton = styled(SocialButton)(({ theme }) => ({
  backgroundColor: "#fff",
  color: "#757575",
  border: "1px solid #dadce0",
  "&:hover": {
    backgroundColor: "#f8f9fa",
    border: "1px solid #dadce0",
  },
}));

const FacebookButton = styled(SocialButton)(({ theme }) => ({
  backgroundColor: "#1877f2",
  color: "white",
  "&:hover": {
    backgroundColor: "#166fe5",
  },
}));

interface LoginPageProps {
  onLogin?: (user: any) => void;
}

interface LoginFormData {
  email: string;
  password: string;
}

interface GoogleResponse {
  credential: string;
}

interface FacebookResponse {
  accessToken?: string;
  userID?: string;
  name?: string;
  email?: string;
  picture?: {
    data?: {
      url?: string;
    };
  };
}

// Cookie configuration
const COOKIE_OPTIONS = {
  expires: 30, // 30 days
  secure: process.env.NODE_ENV === "production",
  sameSite: "strict" as const,
};

const REMEMBER_ME_COOKIE = "datastudio_remember_me";
const SAVED_EMAIL_COOKIE = "datastudio_saved_email";

// Extend Window interface for Google Sign-In
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: () => void;
          renderButton: (element: HTMLElement, config: any) => void;
        };
      };
    };
    FB?: {
      init: (config: any) => void;
      login: (callback: (response: any) => void, options?: any) => void;
      api: (path: string, callback: (response: any) => void) => void;
    };
  }
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  console.log("🚀 LoginPage: Component initializing");

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isFacebookLoading, setIsFacebookLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  console.log("📊 LoginPage: Initial state:", {
    formData,
    showPassword,
    isLoading,
    error,
    isGoogleLoading,
    isFacebookLoading,
    rememberMe,
  });

  // Load saved credentials from cookies on component mount
  useEffect(() => {
    console.log("🍪 LoginPage: Loading saved credentials from cookies");
    const savedRememberMe = Cookies.get(REMEMBER_ME_COOKIE);
    const savedEmail = Cookies.get(SAVED_EMAIL_COOKIE);

    console.log("🍪 LoginPage: Cookie values:", {
      savedRememberMe,
      savedEmail,
    });

    if (savedRememberMe === "true" && savedEmail) {
      console.log("✅ LoginPage: Restoring saved credentials");
      setRememberMe(true);
      setFormData((prev) => {
        const newData = { ...prev, email: savedEmail };
        console.log("📝 LoginPage: Updated form data:", newData);
        return newData;
      });
    }
  }, []);

  // Initialize Google Sign-In
  useEffect(() => {
    console.log("🔧 LoginPage: Initializing Google Sign-In");

    const initializeGoogleSignIn = () => {
      if (window.google && process.env.REACT_APP_GOOGLE_CLIENT_ID) {
        console.log("✅ LoginPage: Google API available, initializing...");
        try {
          window.google.accounts.id.initialize({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            callback: handleGoogleResponse,
          });
          console.log("✅ LoginPage: Google Sign-In initialized successfully");
        } catch (error) {
          console.error(
            "❌ LoginPage: Google Sign-In initialization error:",
            error,
          );
        }
      } else {
        console.warn(
          "⚠️ LoginPage: Google API not available or client ID missing",
        );
      }
    };

    // Load Google Sign-In script
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleSignIn;
    script.onerror = (error) => {
      console.error(
        "❌ LoginPage: Failed to load Google Sign-In script:",
        error,
      );
    };

    document.head.appendChild(script);

    return () => {
      try {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      } catch (error) {
        console.warn(
          "⚠️ LoginPage: Failed to remove Google Sign-In script:",
          error,
        );
      }
    };
  }, []);

  // Initialize Facebook SDK
  useEffect(() => {
    console.log("🔧 LoginPage: Initializing Facebook SDK");

    const initializeFacebookSDK = () => {
      if (window.FB && process.env.REACT_APP_FACEBOOK_APP_ID) {
        console.log("✅ LoginPage: Facebook SDK available, initializing...");
        try {
          window.FB.init({
            appId: process.env.REACT_APP_FACEBOOK_APP_ID,
            cookie: true,
            xfbml: true,
            version: "v18.0",
          });
          console.log("✅ LoginPage: Facebook SDK initialized successfully");
        } catch (error) {
          console.error(
            "❌ LoginPage: Facebook SDK initialization error:",
            error,
          );
        }
      } else {
        console.warn(
          "⚠️ LoginPage: Facebook SDK not available or app ID missing",
        );
      }
    };

    // Load Facebook SDK script
    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.defer = true;
    script.onload = initializeFacebookSDK;
    script.onerror = (error) => {
      console.error("❌ LoginPage: Failed to load Facebook SDK:", error);
    };

    document.head.appendChild(script);

    return () => {
      try {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      } catch (error) {
        console.warn(
          "⚠️ LoginPage: Failed to remove Facebook SDK script:",
          error,
        );
      }
    };
  }, []);

  const saveCredentials = (email: string) => {
    console.log("💾 LoginPage: Saving credentials:", { email, rememberMe });

    if (rememberMe) {
      Cookies.set(REMEMBER_ME_COOKIE, "true", COOKIE_OPTIONS);
      Cookies.set(SAVED_EMAIL_COOKIE, email, COOKIE_OPTIONS);
      console.log("✅ LoginPage: Credentials saved successfully");
    } else {
      Cookies.remove(REMEMBER_ME_COOKIE);
      Cookies.remove(SAVED_EMAIL_COOKIE);
    }
  };

  const navigateToDashboard = () => {
    console.log("🧭 LoginPage: Navigating to dashboard");
    navigate("/dashboard", { replace: true });
    console.log("✅ LoginPage: Navigation to dashboard initiated");
  };

  // Handle Google Sign-In response
  const handleGoogleResponse = async (response: GoogleResponse) => {
    console.log("🔐 LoginPage: Google Sign-In response received");
    setIsGoogleLoading(true);
    setError(null);

    try {
      if (!response.credential) {
        throw new Error("Nu s-a primit credentialul Google.");
      }

      // Decode JWT to get user info
      const decodeJwt = (token: string) => {
        try {
          const base64Url = token.split(".")[1];
          const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split("")
              .map(function (c) {
                return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
              })
              .join(""),
          );
          return JSON.parse(jsonPayload);
        } catch (e) {
          return null;
        }
      };

      const userInfo = decodeJwt(response.credential);
      if (!userInfo) {
        throw new Error("Nu s-au putut extrage datele utilizatorului Google.");
      }

      const user = {
        id: userInfo.sub,
        name: userInfo.name,
        email: userInfo.email,
        picture: userInfo.picture,
        provider: "google",
        token: response.credential,
      };

      // Save email if remember me is checked
      if (rememberMe && user.email) {
        saveCredentials(user.email);
      }

      // Store authentication data
      //localStorage.setItem("authToken", response.credential);
      localStorage.setItem("user", JSON.stringify(user));

      console.log("✅ LoginPage: Google login successful");

      if (onLogin) {
        onLogin(user);
      }

      // Navigate to dashboard
      navigateToDashboard();
    } catch (err) {
      console.error("❌ LoginPage: Google Sign-In error:", err);
      setError("Eroare la autentificarea cu Google");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    console.log("📝 LoginPage: Input change:", { name, value });

    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleRememberMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    console.log("☑️ LoginPage: Remember me changed:", checked);
    setRememberMe(checked);

    // If unchecked, remove saved credentials
    if (!checked) {
      Cookies.remove(REMEMBER_ME_COOKIE);
      Cookies.remove(SAVED_EMAIL_COOKIE);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("📧 LoginPage: Form submission started");

    setIsLoading(true);
    setError(null);

    try {
      // Basic validation
      if (!formData.email || !formData.password) {
        throw new Error("Vă rugăm să completați toate câmpurile");
      }

      if (!formData.email.includes("@")) {
        throw new Error("Adresa de email nu este validă");
      }

      console.log("✅ LoginPage: Validation passed");

      // Get API settings from environment variables
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      const loginEndpoint = process.env.REACT_APP_LOGIN_ENDPOINT;
      const apiTimeout = parseInt(process.env.REACT_APP_API_TIMEOUT || "10000");

      if (!apiBaseUrl || !loginEndpoint) {
        throw new Error(t("login.errors.apiConfigIncomplete"));
      }

      // Prepare API request
      const loginUrl = `${apiBaseUrl}${loginEndpoint}`;
      const requestBody = {
        username: formData.email,
        password: formData.password,
        rememberMe: rememberMe,
      };

      console.log("🌐 LoginPage: Making API call to:", loginUrl);

      // Make API call
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), apiTimeout);

      const response = await fetch(loginUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      console.log("📥 LoginPage: API response received:", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
      });

      if (!response.ok) {
        switch (response.status) {
          case 401:
            throw new Error(t("login.errors.invalidCredentials"));
          case 403:
            throw new Error("Accesul este interzis");
          case 404:
            throw new Error("Serviciul de autentificare nu a fost găsit");
          case 500:
            throw new Error("Eroare internă a serverului");
          default:
            throw new Error(
              `Eroare la autentificare: ${response.status} ${response.statusText}`,
            );
        }
      }

      // Parse response
      const result = await response.json();
      console.log("📋 LoginPage: Parsed API response:", result);

      // Check if API returned success
      if (result.success === false) {
        throw new Error(result.message || t("login.errors.authFailed"));
      }

      // Save credentials if remember me is checked
      saveCredentials(formData.email);

      // Create user object from API response
      const user = {
        id: result.user?.id || result.userId || "1",
        name:
          result.name ||
          result.user?.displayName ||
          formData.email.split("@")[0],
        email: result.user?.email || formData.email,
        token: result.token,
        provider: "api",
        ...result.user,
      };

      console.log("✅ LoginPage: API login successful");

      // Store authentication token
      if (result.token) {
        const tokenData = result.token;
        console.log(
          "🔍 LoginPage: Token data type:",
          typeof tokenData,
          tokenData,
        );
        // Handle token as object with Token, TokenExpires, TokenHasExpired fields
        // Clear old token format before storing new one
        localStorage.removeItem("authToken");
        localStorage.removeItem("tokenExpires");
        localStorage.removeItem("tokenHasExpired");
        localStorage.removeItem("user");
        if (typeof tokenData == "object" && tokenData.token) {
          localStorage.setItem("authToken", tokenData.token);
          localStorage.setItem("tokenExpires", tokenData.tokenExpires);
          localStorage.setItem(
            "tokenHasExpired",
            String(tokenData.tokenHasExpired),
          );
          console.log("💾 LoginPage: Token object stored in localStorage", {
            expires: tokenData.tokenExpires,
            hasExpired: tokenData.tokenHasExpired,
          });
        } else {
          // Fallback for string token
          localStorage.setItem("authToken", String(tokenData));
          console.log("💾 LoginPage: Token string stored in localStorage");
        }

        localStorage.setItem("user", JSON.stringify(user));
        console.log("💾 LoginPage: User data stored in localStorage");
      }

      if (onLogin) {
        console.log("🔄 LoginPage: Calling onLogin callback");
        onLogin(user);
      }

      // Navigate to dashboard
      navigateToDashboard();
    } catch (err) {
      console.error("❌ LoginPage: API login error:", err);

      if (err instanceof TypeError && err.message.includes("fetch")) {
        setError(
          "Nu se poate conecta la server. Verificați conexiunea la internet.",
        );
      } else if (err instanceof Error && err.name === "AbortError") {
        setError("Timpul de așteptare a expirat. Încercați din nou.");
      } else {
        setError(
          err instanceof Error ? err.message : "Eroare la autentificare",
        );
      }
    } finally {
      console.log("🏁 LoginPage: API login process finished");
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    console.log("🔐 LoginPage: Google Sign-In button clicked");
    setIsGoogleLoading(true);

    if (window.google) {
      try {
        window.google.accounts.id.prompt();
        console.log("✅ LoginPage: Google sign-in prompt triggered");
      } catch (error) {
        console.error("❌ LoginPage: Error triggering Google sign-in:", error);
        setIsGoogleLoading(false);
        setError("Eroare la inițializarea Google Sign-In");
      }
    } else {
      console.error("❌ LoginPage: Google API not available");
      setIsGoogleLoading(false);
      setError("Google Sign-In nu este disponibil");
    }
  };

  console.log("🎨 LoginPage: Rendering component");

  return (
    <LoginContainer>
      <LoginCard>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              fontWeight="bold"
            >
              {t("login.title")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t("login.subtitle")}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              name="email"
              type="email"
              label={t("login.email")}
              value={formData.email}
              onChange={handleInputChange}
              margin="normal"
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              name="password"
              type={showPassword ? "text" : "password"}
              label={t("login.password")}
              value={formData.password}
              onChange={handleInputChange}
              margin="normal"
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={isLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
                mb: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    name="rememberMe"
                    color="primary"
                    disabled={isLoading}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    {t("login.rememberMe")}
                  </Typography>
                }
              />

              <Link
                href="#"
                variant="body2"
                color="primary"
                onClick={(e) => {
                  e.preventDefault();
                  console.log("🔗 LoginPage: Forgot password link clicked");
                }}
              >
                {t("login.forgotPassword")}
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{ mt: 2, mb: 2, height: 48 }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t("login.loggingIn")}
                </>
              ) : (
                t("login.loginButton")
              )}
            </Button>
          </Box>

          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t("login.noAccount")}{" "}
              <Link
                href="#"
                color="primary"
                fontWeight="medium"
                onClick={(e) => {
                  e.preventDefault();
                  console.log("🔗 LoginPage: Register link clicked");
                  navigate("/register");
                }}
              >
                {t("login.signUp")}
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </LoginCard>
      {/* Footer */}
      <Box
        component="footer"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          py: 2,
          px: 3,
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          backdropFilter: "blur(10px)",
          borderTop: "1px solid rgba(255, 255, 255, 0.2)",
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "rgba(255, 255, 255, 0.9)", fontWeight: 500 }}
          >
            © 2025 DataStudio Project. All rights reserved.
          </Typography>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Button
              href="https://www.datastudio.ro/api/swagger/index.html"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{
                textTransform: "none",
                color: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  color: "#fff",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              DSC Web Api Swagger
            </Button>
            <Button
              href="https://datastudio.ro/client"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{
                textTransform: "none",
                color: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  color: "#fff",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              Zona client
            </Button>
            <Button
              href="http://datastudio.ro/Reports"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{
                textTransform: "none",
                color: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  color: "#fff",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              Rapoarte
            </Button>
            <Button
              href="https://datastudio.ro/eCommerce"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{
                textTransform: "none",
                color: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  color: "#fff",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              eCommerce Portal
            </Button>
            <Button
              href="https://stackoverflow.com"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{
                textTransform: "none",
                color: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  color: "#fff",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              Stack Overflow
            </Button>
          </Box>
        </Box>
      </Box>
    </LoginContainer>
  );
};

export default LoginPage;
